// SF1 + SF2 — Sales and Units Trend Chart.
//
// One component, two views. Revenue and units are the same chart with a
// different metric, which is why the PRD scopes them as a single sub-feature and
// why one request carries both series.
//
// Returns are plotted against sales and never netted off them: the gap between
// the two lines is the point of the chart. A returns series sitting at zero is a
// result and renders as zero, while a returns feed that has not synced is absent
// and renders as nothing at all. The two must not look alike.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../SalesPerformance.module.css';
import { Change, ClippedWindow, formatRange } from './format';
import { getTrend } from '../services/salesPerformanceApi';

// Revenue carries a profit line in the PRD; units has no unit form of profit, so
// the series list differs by view rather than the chart being duplicated.
const VIEWS = {
  revenue: {
    label: 'Revenue',
    primary: { key: 'revenue', name: 'Sales', colour: '#1f6feb' },
    secondary: { key: 'returns_value', name: 'Returns', colour: '#d1495b' },
    totalKey: 'revenue',
    changeKey: 'revenue_change',
    money: true,
  },
  units: {
    label: 'Units',
    primary: { key: 'units', name: 'Units ordered', colour: '#1f6feb' },
    secondary: { key: 'returns_units', name: 'Units returned', colour: '#d1495b' },
    totalKey: 'units',
    changeKey: 'units_change',
    money: false,
  },
};

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const formatValue = (value, money) => {
  if (value === null || value === undefined) return '—';
  return money ? `₹${inr.format(Math.round(value))}` : inr.format(value);
};

// The backend stamps sync times in UTC without a zone marker, which the browser
// would otherwise read as local time. Shown in IST, as the PRD requires.
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

const formatSyncedAt = (iso) => {
  if (!iso) return 'never';
  const date = new Date(HAS_ZONE.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  })} IST`;
};

// A sync that failed after an earlier one landed leaves true but old figures on
// the page. Said on the surface so stale data is never read as current.
const FailedSyncs = ({ freshness }) => {
  const failed = freshness?.failed_syncs || [];
  if (!failed.length) return null;
  return (
    <div className={`${local.banner} ${local.bannerWarn}`}>
      <FiAlertTriangle className={local.bannerIcon} />
      <div>
        <strong>Latest sync failed</strong>
        {failed.map((entry) => {
          const lastGood = freshness[`${entry.source}_synced_at`];
          return (
            <div key={entry.source}>
              The latest {entry.source} sync failed
              {entry.segments ? ` for ${entry.segments_failed} of ${entry.segments} history periods` : ''}
              {' '}on {formatSyncedAt(entry.failed_at)}.
              {lastGood
                ? ` Its figures are from the last sync that succeeded, ${formatSyncedAt(lastGood)}, and may be out of date.`
                : ` No ${entry.source} have synced successfully yet.`}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Hover detail: the date and every series value at that point, plus a marker
// when the bucket is clipped by a window edge or by where the synced data stops,
// so a short bar is not read as a drop in trading.
//
// A bucket the reports do not reach at all carries no values and says so. It is
// the one state that must never be confused with a zero.
const PointDetail = ({ active, payload, label, money }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  if (point.covered === 'none') {
    return (
      <div className={local.tooltip}>
        <div className={local.tooltipHead}>{label}</div>
        <div className={local.tooltipRow}>
          <span>No synced data for this period yet</span>
        </div>
      </div>
    );
  }
  return (
    <div className={local.tooltip}>
      <div className={local.tooltipHead}>
        {label}
        {point.partial && <span className={local.tooltipPartial}>partial</span>}
      </div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className={local.tooltipRow}>
          <span className={local.tooltipSwatch} style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <strong>{formatValue(entry.value, money && entry.dataKey !== 'units'
            && entry.dataKey !== 'returns_units')}</strong>
        </div>
      ))}
    </div>
  );
};

// window and granularity arrive from the page: the PRD puts both above the page
// governing every component, so the chart never owns the period it reports.
// The metric and shape toggles below are the chart's own and stay here.
const TrendChart = ({ window: reportingWindow, onLoadingChange, granularity }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;

  const [view, setView] = useState('revenue');
  const [shape, setShape] = useState('line');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { start, end } = reportingWindow || {};

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getTrend(token, { start, end, granularity }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, granularity, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const config = VIEWS[view];

  // The backend states which series it can actually draw, so a missing returns
  // feed removes the series rather than plotting a flat line at zero.
  const drawable = data?.series?.[view] || [];
  const showSecondary = drawable.includes(config.secondary.key);

  const total = data?.totals?.[config.totalKey];
  const change = data?.totals?.[config.changeKey];

  const points = useMemo(() => data?.points || [], [data]);
  // Buckets the synced reports do not reach carry null, and recharts leaves a
  // gap for them. Drawing straight through would join two real points across a
  // period nobody measured.
  const hasGap = useMemo(
    () => points.some((point) => point.covered === 'none'), [points],
  );

  const Primary = shape === 'bar' ? Bar : Line;

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Sales and units trend</h2>
        <div className={local.headAside}>
          <div className={local.segmented}>
            {Object.entries(VIEWS).map(([key, entry]) => (
              <button
                key={key}
                type="button"
                className={key === view ? local.segmentOn : local.segment}
                onClick={() => setView(key)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <div className={local.segmented}>
            {['line', 'bar'].map((option) => (
              <button
                key={option}
                type="button"
                className={option === shape ? local.segmentOn : local.segment}
                onClick={() => setShape(option)}
              >
                {option === 'line' ? 'Line' : 'Bar'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={local.freshness}>
        Orders synced {formatSyncedAt(data?.freshness?.orders_synced_at)}
        {data?.freshness?.report_lag_days
          ? ` · Amazon's report trails live trading by about ${data.freshness.report_lag_days} days`
          : ''}
        {data?.coverage
          ? ` · data covers ${formatRange(data.coverage.start, data.coverage.end)}`
          : ''}
      </div>

      <FailedSyncs freshness={data?.freshness} />

      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the trend'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <>
          <div className={local.totals}>
            <div>
              <div className={local.totalLabel}>
                {config.label} in this period
              </div>
              <div className={local.totalValue}>
                {formatValue(total, config.money)}
              </div>
            </div>
            <div>
              <div className={local.totalLabel}>
                {data?.comparison?.available
                  ? `Against ${formatRange(data.comparison.start, data.comparison.end)}`
                  : 'Against the prior period'}
              </div>
              <div className={local.totalValue}>
                <Change value={change} large absent="Not enough history" />
              </div>
            </div>
            {showSecondary && (
              <div>
                <div className={local.totalLabel}>Returns in this period</div>
                <div className={local.totalValue}>
                  {formatValue(
                    data?.totals?.[view === 'revenue' ? 'returns_value' : 'returns_units'],
                    config.money,
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={local.plotLive}>
            {!loading && !points.some((point) => point.covered !== 'none') ? (
              <div className={local.plotEmpty}>
                {data?.coverage
                  ? `No synced orders fall in this period. Data covers
                     ${formatRange(data.coverage.start, data.coverage.end)}.`
                  : 'No orders in this period.'}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke="#eef0f3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false} axisLine={{ stroke: '#e3e6ea' }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickLine={false}
                    axisLine={false} width={70}
                    tickFormatter={(value) => formatValue(value, config.money)} />
                  <Tooltip content={<PointDetail money={config.money} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Primary
                    type="monotone"
                    dataKey={config.primary.key}
                    name={config.primary.name}
                    stroke={config.primary.colour}
                    fill={config.primary.colour}
                    strokeWidth={2}
                    // A gap needs its surviving points marked, or a single
                    // covered bucket between two uncovered ones draws nothing.
                    dot={hasGap && shape !== 'bar' ? { r: 2 } : false}
                    connectNulls={false}
                    radius={shape === 'bar' ? [3, 3, 0, 0] : undefined}
                  />
                  {showSecondary && (
                    <Line
                      type="monotone"
                      dataKey={config.secondary.key}
                      name={config.secondary.name}
                      stroke={config.secondary.colour}
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}

      {/* Every approximation and omission the figures carry, stated rather than
          buried, because a seller reconciling against Seller Central needs to
          know what is not in the number. */}
      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default TrendChart;
